import { useState } from "react";
import {
  useAdmins,
  useCreateAdmin,
  useChangeAdminRole,
  useToggleAdminDisabled,
  type AdminRow,
} from "../hooks/useAdmins.js";
import { formatDate, formatRelative } from "../lib/format.js";
import { getCurrentAdmin } from "../lib/auth.js";
import { avatarColor } from "../lib/avatar-color.js";

export function AdminsPage() {
  const me = getCurrentAdmin();
  const isOwner = me?.role === "owner";

  const { data, isLoading, error } = useAdmins();
  const [showCreate, setShowCreate] = useState(false);

  if (!isOwner) {
    return (
      <div>
        <h1 className="page-title">管理员</h1>
        <div className="err" style={{ marginTop: 16 }}>
          只有 owner 角色能访问此页
        </div>
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: "flex", alignItems: "flex-end", gap: 16 }}>
        <div style={{ flex: 1 }}>
          <h1 className="page-title">管理员</h1>
          <p className="page-sub">
            hub 内部账号 · {data?.length ?? 0} 个 · 独立于 mhwork 终端用户体系
          </p>
        </div>
        <button className="btn btn-primary" onClick={() => setShowCreate(true)}>
          + 新增管理员
        </button>
      </div>

      {isLoading && <div className="empty">加载中…</div>}
      {error && <div className="err" style={{ marginTop: 20 }}>{(error as Error).message}</div>}

      {data && data.length > 0 && (
        <table className="table" style={{ marginTop: 20 }}>
          <thead>
            <tr>
              <th>管理员</th>
              <th>角色</th>
              <th>上次登录</th>
              <th>加入</th>
              <th>状态</th>
              <th style={{ width: 100 }}></th>
            </tr>
          </thead>
          <tbody>
            {data.map((a) => (
              <AdminRowItem key={a.id} admin={a} isSelf={a.id === me?.id} />
            ))}
          </tbody>
        </table>
      )}

      {showCreate && <CreateAdminModal onClose={() => setShowCreate(false)} />}
    </div>
  );
}

function AdminRowItem({ admin, isSelf }: { admin: AdminRow; isSelf: boolean }) {
  const changeRole = useChangeAdminRole();
  const toggleDis = useToggleAdminDisabled();

  return (
    <tr style={{ opacity: admin.disabled ? 0.55 : 1 }}>
      <td>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div
            className="avatar"
            style={{
              width: 36,
              height: 36,
              fontSize: 14,
              background: avatarColor(admin.email),
            }}
          >
            {(admin.display_name || admin.email)[0]!.toUpperCase()}
          </div>
          <div>
            <div style={{ fontSize: 13, fontWeight: 500 }}>
              {admin.display_name || "—"}
              {isSelf && (
                <span
                  className="pill pill-primary"
                  style={{ marginLeft: 6, fontSize: 10 }}
                >
                  你
                </span>
              )}
            </div>
            <div
              className="mono"
              style={{ fontSize: 11, color: "var(--text-muted)" }}
            >
              {admin.email}
            </div>
          </div>
        </div>
      </td>
      <td>
        <select
          value={admin.role}
          onChange={(e) =>
            changeRole.mutate({ id: admin.id, role: e.target.value as AdminRow["role"] })
          }
          disabled={isSelf}
          style={{ width: 120, height: 30, fontSize: 12 }}
        >
          <option value="owner">owner</option>
          <option value="publisher">publisher</option>
          <option value="reviewer">reviewer</option>
          <option value="viewer">viewer</option>
        </select>
      </td>
      <td style={{ fontSize: 12, color: "var(--text-muted)" }}>
        {formatRelative(admin.last_login_at)}
      </td>
      <td style={{ fontSize: 12, color: "var(--text-muted)" }}>
        {formatDate(admin.created_at)}
      </td>
      <td>
        {admin.disabled ? (
          <span className="pill pill-danger">已禁用</span>
        ) : (
          <span className="pill pill-success">活跃</span>
        )}
      </td>
      <td>
        {!isSelf && (
          <button
            className={admin.disabled ? "btn btn-primary btn-xs" : "btn btn-danger btn-xs"}
            onClick={() =>
              toggleDis.mutate({ id: admin.id, disabled: admin.disabled === 0 })
            }
          >
            {admin.disabled ? "启用" : "禁用"}
          </button>
        )}
      </td>
    </tr>
  );
}

function CreateAdminModal({ onClose }: { onClose: () => void }) {
  const create = useCreateAdmin();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [role, setRole] = useState<AdminRow["role"]>("reviewer");
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      await create.mutateAsync({ email, password, displayName, role });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "创建失败");
    }
  };

  return (
    <div className="modal-mask" onClick={onClose}>
      <form
        className="modal"
        style={{ display: "flex", flexDirection: "column", gap: 14 }}
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
      >
        <div>
          <h2 style={{ fontSize: 17, margin: 0, fontWeight: 600 }}>新增管理员</h2>
          <p className="page-sub">邀请一名内部员工,初始密码你指定,登录后能自己改</p>
        </div>

        <Field label="邮箱">
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="name@metrichub.app"
          />
        </Field>
        <Field label="初始密码(≥ 10 位)">
          <input
            type="password"
            minLength={10}
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </Field>
        <Field label="显示名(可选)">
          <input
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="张三"
          />
        </Field>
        <Field label="角色">
          <select value={role} onChange={(e) => setRole(e.target.value as AdminRow["role"])}>
            <option value="owner">owner — 全权限(管理员管理)</option>
            <option value="publisher">publisher — 发布 / 下架 / 审核</option>
            <option value="reviewer">reviewer — 仅审核</option>
            <option value="viewer">viewer — 只读</option>
          </select>
        </Field>

        {error && <div className="err">{error}</div>}

        <div style={{ display: "flex", gap: 10, marginTop: 8 }}>
          <button type="button" className="btn btn-secondary" style={{ flex: 1 }} onClick={onClose}>
            取消
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            style={{ flex: 2 }}
            disabled={create.isPending}
          >
            {create.isPending ? "创建中…" : "创建"}
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 5 }}>
      <span style={{ fontSize: 12, fontWeight: 500, color: "var(--text-muted)" }}>
        {label}
      </span>
      {children}
    </label>
  );
}
